import { useEffect, useState } from 'react';

import constants from '../constants';

import { useNavigate } from 'react-router';
import { useTestState } from '../context/TestContext';
import i18n from '../i18n';
import { hmacEncode, sanitizePayload } from '../utils';
import {
  buildShareableResultsUrl,
  saveDetailedResults,
} from '../utils/detailedResultsStorage';
import useTracking from './useTracking';
import { useLogError } from './useLogError';

const maxTrialIndex = constants['maxTrials'] - 1; // 20 trials max per test production

interface TestData {
  trialIndex: number;
  loudness: number;
  frequency: string;
  didUserRespond: boolean;
  responseTime: number;
  interval: number;
}

const useTest = ({
  isB2b2c,
  isB2b,
  algorithmValidationOnly = false,
}: {
  isB2b2c: boolean;
  isB2b: boolean;
  algorithmValidationOnly?: boolean;
}) => {
  const [testData, setTestData] = useState<TestData[]>([]);

  // start the test sequence at 45 dB
  // go up in increments of 20 dB until they record their first hit
  // then proceed as usual: down 10, up 5
  const [loudness, setLoudness] = useState(45);
  const [frequency, setFrequency] = useState('R1000');
  const [freqList, setFreqList] = useState(constants['freqList']);
  const [isTraining, setIsTraining] = useState(true);
  const [isRetest, setIsRetest] = useState(false);
  const [dataPosted, setDataPosted] = useState(false);

  // const [retestList, setRetestList] = useState([])

  const { state, dispatch } = useTestState();
  const { trackEvent } = useTracking();
  const navigate = useNavigate();
  const { logError } = useLogError();

  useEffect(() => {
    if (isTraining) {
      // start training
      dispatch({ type: 'UPDATE_PROGRESS', payload: 15 });
      dispatch({ type: 'UPDATE_CURRENT_STEP', payload: 1 });
    }
    if (!isTraining && !state.finishTrainingCalled) {
      trackEvent('JHT_TEST_START');

      // finish training
      dispatch({ type: 'UPDATE_PROGRESS', payload: 0 });
      dispatch({ type: 'UPDATE_CURRENT_STEP', payload: 2 });
      dispatch({
        type: 'UPDATE_FINISH_TRAINING_CALLED',
        payload: true,
      });
    }
    if (frequency[0] === 'L' && !state.nextEarCalled) {
      trackEvent('JHT_HALFWAY_THROUGH_TEST');

      // move to next ear
      dispatch({ type: 'UPDATE_PROGRESS', payload: 0 });
      dispatch({ type: 'UPDATE_CURRENT_STEP', payload: 3 });
      dispatch({ type: 'UPDATE_NEXT_EAR_CALLED', payload: true });
    }
    if (isRetest) {
      // start retest
      dispatch({ type: 'UPDATE_PROGRESS', payload: 0 });
      dispatch({ type: 'UPDATE_CURRENT_STEP', payload: 4 });
    }
  }, [
    frequency,
    isTraining,
    isRetest,
    state.finishTrainingCalled,
    state.nextEarCalled,
    state.email,
    // dispatch,
    // trackEvent,
  ]);

  const updateTestData = ({
    trialIndex,
    loudness,
    frequency,
    didUserRespond,
    responseTime,
    interval,
  }: TestData) => {
    const updatedTestData = [
      ...testData,
      {
        trialIndex,
        loudness,
        frequency,
        responseTime,
        interval,
        didUserRespond,
      },
    ];
    setTestData(updatedTestData);
  };

  const resetTestData = () => {
    setTestData([]);
  };

  // returns the total number of presentaions at a given loudness
  // ZMTODO: could refactor to one line
  const numTrialsAtLoudness = (testData: TestData[], loudness: number) => {
    const responseArray = testData
      .filter((trial) => trial.loudness === loudness)
      .map((trial) => trial.didUserRespond);

    return responseArray.length;
  };

  // returns the proportion of true responses to total presentations at a given loudness
  const numHitsAtLoudness = (testData: TestData[], loudness: number) => {
    const responseArray = testData
      .filter((trial) => trial.loudness === loudness)
      .map((trial) => trial.didUserRespond);

    if (responseArray.length === 0) {
      return null;
    }

    return responseArray.reduce((count, value) => count + (value ? 1 : 0), 0);
  };

  // returns the proportion of true responses to total presentations at a given loudness
  const hitProportionAtLoudness = (testData: TestData[], loudness: number) => {
    const responseArray = testData
      .filter((trial) => trial.loudness === loudness)
      .map((trial) => trial.didUserRespond);

    if (responseArray.length === 0) {
      return null;
    }

    const trueCount = responseArray.reduce(
      (count, value) => count + (value ? 1 : 0),
      0,
    );
    return trueCount / responseArray.length;
  };

  // return score 1-5 based on threshold value
  const scoreFromThreshold = (threshold?: number | null) => {
    // this shouldn't ever be called
    if (!threshold) return 5;

    if (threshold <= 25) {
      return 5;
    } else if (threshold <= 40) {
      return 4;
    } else if (threshold <= 60) {
      return 3;
    } else if (threshold <= 90) {
      return 2;
    } else return 5;
    // should never reach here
    // finding that it does in specific case: when you select 'Skip' on last frequency (L8000) - JP
    // TODO: why?
    // return a score of 5
    //console.log('no threshold condition met for threshold value:', threshold)
  };

  const getOverallScore = () => {
    const hrData = state.hearingresultdata;

    // in the event that last test sequence is skipped (L8000)
    // this value will be null, so let's correctly replace it with 90
    let thresholdL8000 = hrData?.first_test_left_8000__c;
    if (!thresholdL8000) {
      thresholdL8000 = 90;
    }

    const scoreLeft = {
      500: scoreFromThreshold(hrData?.first_test_left_500__c),
      1000: scoreFromThreshold(hrData?.first_test_left_1000__c),
      2000: scoreFromThreshold(hrData?.first_test_left_2000__c),
      3000: scoreFromThreshold(hrData?.first_test_left_3000__c),
      4000: scoreFromThreshold(hrData?.first_test_left_4000__c),
      8000: scoreFromThreshold(thresholdL8000),
    };

    const scoreRight = {
      500: scoreFromThreshold(hrData?.first_test_right_500__c),
      1000: scoreFromThreshold(hrData?.first_test_right_1000__c),
      2000: scoreFromThreshold(hrData?.first_test_right_2000__c),
      3000: scoreFromThreshold(hrData?.first_test_right_3000__c),
      4000: scoreFromThreshold(hrData?.first_test_right_4000__c),
      8000: scoreFromThreshold(hrData?.first_test_right_8000__c),
    };

    const minValue = Math.min(
      ...Object.values(scoreLeft),
      ...Object.values(scoreRight),
    );

    return minValue;
  };

  const getResultsUrl = () => {
    const hrData = state.hearingresultdata;

    // Variable Pure Tone Average (VPTA) suggested by Clark et. al. in the ASHA journal
    // https://www.researchgate.net/publication/16145943_Uses_and_abuses_of_hearing_loss_classification
    // see results page overhaul summary doc for more info:
    // https://docs.google.com/document/d/1zp7fHbkr543Pr0jo2XkYYhFbcMnd2pQZuXt-VEg4e6k/edit

    const calculateAverage = (values: number[]) =>
      values.reduce((a, b) => a + b, 0) / values.length;

    // Extract frequency measurements for both ears
    // ZMTODO: figure out cleaner typing for hrData
    // these will never actually be zero, but typescript complains
    const leftFrequencies = [
      hrData?.first_test_left_500__c ?? 0,
      hrData?.first_test_left_1000__c ?? 0,
      hrData?.first_test_left_2000__c ?? 0,
      hrData?.first_test_left_3000__c ?? 0,
      hrData?.first_test_left_4000__c ?? 0,
    ];
    const rightFrequencies = [
      hrData?.first_test_right_500__c ?? 0,
      hrData?.first_test_right_1000__c ?? 0,
      hrData?.first_test_right_2000__c ?? 0,
      hrData?.first_test_right_3000__c ?? 0,
      hrData?.first_test_right_4000__c ?? 0,
    ];
    const leftHighFrequencies = [
      hrData?.first_test_left_3000__c ?? 0,
      hrData?.first_test_left_4000__c ?? 0,
      hrData?.first_test_left_8000__c ?? 0,
    ];
    const rightHighFrequencies = [
      hrData?.first_test_right_3000__c ?? 0,
      hrData?.first_test_right_4000__c ?? 0,
      hrData?.first_test_right_8000__c ?? 0,
    ];

    // Calculate the average of the 3 highest values among 500, 1000, 2000, and 4000 Hz
    // ensure it is an integer by rounding it
    const vPTALeft = Math.round(
      calculateAverage(leftFrequencies.sort((a, b) => b - a).slice(0, 3)),
    );
    const vPTARight = Math.round(
      calculateAverage(rightFrequencies.sort((a, b) => b - a).slice(0, 3)),
    );

    // integer between 10 and 90
    // const vPTAOverall = Math.round(calculateAverage([vPTALeft, vPTARight]));
    // we want to classify the max value of the 2 results in showing the user the overall summary
    const vPTAMax = Math.max(vPTALeft, vPTARight);
    dispatch({ type: 'UPDATE_VPTAOVERALL', payload: vPTAMax });
    dispatch({ type: 'UPDATE_VPTALEFT', payload: vPTALeft });
    dispatch({ type: 'UPDATE_VPTARIGHT', payload: vPTARight });

    // Values for 500 Hz for both ears for Low
    const vPTALowLeft = hrData?.first_test_left_500__c;
    const vPTALowRight = hrData?.first_test_right_500__c;

    // Averages for 1000 and 2000 Hz for both ears for Mid
    const vPTAMidLeft = Math.round(
      calculateAverage([
        hrData?.first_test_left_1000__c ?? 0,
        hrData?.first_test_left_2000__c ?? 0,
      ]),
    );
    const vPTAMidRight = Math.round(
      calculateAverage([
        hrData?.first_test_right_1000__c ?? 0,
        hrData?.first_test_right_2000__c ?? 0,
      ]),
    );

    // Averages of the 2 highest frequencies among 3000, 4000, and 8000 Hz for both ears for High
    const vPTAHighLeft = Math.round(
      calculateAverage(leftHighFrequencies.sort((a, b) => b - a).slice(0, 2)),
    );
    const vPTAHighRight = Math.round(
      calculateAverage(rightHighFrequencies.sort((a, b) => b - a).slice(0, 2)),
    );

    const resultsDict = {
      overall: vPTAMax,
      L: {
        overall: vPTALeft,
        low: vPTALowLeft ?? 0,
        mid: vPTAMidLeft,
        high: vPTAHighLeft,
      },
      R: {
        overall: vPTARight,
        low: vPTALowRight ?? 0,
        mid: vPTAMidRight,
        high: vPTAHighRight,
      },
    };

    const date = Date.now();
    const name = isB2b ? state.nameonresult : state.email;
    const contactid = state.contactid || '';
    const hideButtons = Boolean(isB2b2c || isB2b);

    // Session copy for in-app navigation; shareable URL for Salesforce / Sheets.
    saveDetailedResults({
      date,
      name: name || '',
      contactid,
      hideButtons,
      results: resultsDict,
    });

    // Absolute prod host so partner Sheets always open hearing-test.audicus.com
    // (not a preview/Cloud Run origin captured at test time).
    const shareOrigin =
      process.env.NODE_ENV === 'production'
        ? 'https://hearing-test.audicus.com'
        : window.location.origin;

    return buildShareableResultsUrl({
      origin: shareOrigin,
      date,
      name: name || '',
      contactid,
      hideButtons,
      results: resultsDict,
    });
  };

  const postTestData = async (testData: TestData[], threshold?: number) => {
    // Algorithm validation mode should never persist or submit data.
    if (algorithmValidationOnly) {
      return;
    }

    const contactData = { id: state.contactid };
    const patientData = { id: state.patientid };

    let earStr = '';
    let freqStr = '';
    let trialData = [];

    if (testData.length) {
      earStr = testData[0].frequency[0] === 'R' ? 'right' : 'left';
      freqStr = testData[0].frequency.slice(1);

      trialData = testData.map((trial) => ({
        responseTime: trial.responseTime,
        interval: trial.interval,
        loudness: trial.loudness,
      }));
    } else {
      // for skipping test when loudness is 90
      earStr = freqList[0][0] === 'R' ? 'right' : 'left';
      freqStr = freqList[0].slice(1);

      trialData = [
        {
          responseTime: [],
          interval: [],
          loudness: [],
        },
      ];
    }

    // console.log('current retestList:', retestList)

    const hearingResultData = {
      ...state.hearingresultdata,
      id: state.hearingresultid,
      name_on_result__c: state.nameonresult,
      patient_dob__c: state.dob || state.patientdob,
      trial_information__c: {
        ...state.hearingresultdata?.trial_information__c,
        [`${earStr}_${freqStr}_trialArray`]: trialData,
      },
      [`first_test_${earStr}_${freqStr}__c`]: threshold,
      [`second_test_${earStr}_${freqStr}__c`]: threshold,
      hearing_result_type__c: process.env.REACT_APP_VERSION
        ? `JHT Web App v${process.env.REACT_APP_VERSION}`
        : process.env.REACT_APP_GIT_COMMIT
          ? `JHT Web App ${process.env.REACT_APP_GIT_COMMIT}`
          : 'JHT Web App dev',
      version_id__c: process.env.REACT_APP_VERSION
        ? `${process.env.REACT_APP_VERSION}-${i18n.language}`
        : process.env.REACT_APP_GIT_COMMIT
          ? `${process.env.REACT_APP_GIT_COMMIT}-${i18n.language}`
          : `dev-${i18n.language}`,
      ...(state.createdAtState && { Created_at_State__c: state.createdAtState }),
      // TODO ZM/JP: tmp fix - retestList has repeat values
      // frequencies_retested__c: [...new Set(retestList)].join(", ")
    };

    dispatch({ type: 'UPDATE_HEARINGRESULTDATA', payload: hearingResultData });

    // In local "hearing test only" mode, do not POST activity data to the API.
    if (constants.localHearingOnly) {
      return;
    }

    let activitytypeid = '';
    activitytypeid =
      freqList[0] === 'R8000'
        ? isB2b
          ? 'JHT Web B2B Halfway Complete'
          : 'JHT Web Halfway Complete'
        : 'JHT Web Update Hearing Result';

    // initialize the activityData
    const data = {
      contact: contactData,
      patient__c: patientData,
      hearing_result__c: hearingResultData,
      activitytypeid: activitytypeid,
    };

    const sanitizedData = sanitizePayload(data);
    if (!sanitizedData) {
      return;
    }

    const hmacSecret = constants['hmacSecret'];
    const apiBaseUrl = constants['apiBaseUrl'];
    if (!hmacSecret || !apiBaseUrl) {
      return;
    }

    const activityData = JSON.stringify(sanitizedData);
    const signature = await hmacEncode(activityData, hmacSecret);

    try {
      const response = await fetch(apiBaseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-AUD-Signature': signature,
        },
        body: activityData,
      });

      if (!response?.ok) {
        throw new Error(
          `Failed to POST to ${apiBaseUrl} on ${activitytypeid} for contact id: ${contactData.id}`,
        );
      }
      const responseData = await response.json();
      if (!state.hearingresultid) {
        const hearingResultId = responseData.hearing_result__c.id;
        dispatch({ type: 'UPDATE_HEARINGRESULTID', payload: hearingResultId });
      }
    } catch (error) {
      logError(error);
    }
  };

  // checks various conditions to see if test sequence should stop
  // if so, returns the threshold determination (integer), otherwise returns null
  // Mirror logic in utils/hearingThresholdStopping.ts (and Jest tests there / algorithmValidationSequences.test.ts).
  const checkStoppingCriteria = (testData: TestData[]) => {
    // DEV CODE
    // SPEEDS THROUGH TEST SEQUENCE IN DEV MODE

    if (
      process.env.REACT_APP_USE_SKIP_TESTS === 'true' &&
      !algorithmValidationOnly
    ) {
      if (testData[0].frequency[0] === 'R') {
        // for dev testing validation/retest procedure
        // DEV RETEST CRITERION
        // if (constants['lowFreqList'].includes(testData[0].frequency) && !isTraining) {
        //   setRetestList([...retestList, frequency]);
        // }
        postTestData(testData, 20);
        return 20;
      } else {
        postTestData(testData, 50);
        return 50;
      }
    }

    // DETERMINE CURRENT VALUES OF CRITERIA
    // returns true if the max number of trials has been reached
    const maxTrialCheck = testData.length === maxTrialIndex + 1;

    // returns the total number of hits at 0, 5, or 10 dB
    const hitsBelow15 = testData
      .filter((trial) => trial.loudness < 15)
      .map((trial) => trial.didUserRespond)
      .reduce((count, value) => count + (value ? 1 : 0), 0);

    // returns the total number of misses at 85 dB
    const missesAbove80 = testData
      .filter((trial) => trial.loudness > 80)
      .map((trial) => trial.didUserRespond)
      .reduce((count, value) => count + (value ? 0 : 1), 0);

    // get total number of presentations at current loudness +/- 5 dB around current loudness
    const numTrialsCurrent = numTrialsAtLoudness(testData, loudness);
    const numTrialsLower = numTrialsAtLoudness(testData, loudness - 5);
    const numTrialsUpper = numTrialsAtLoudness(testData, loudness + 5);

    // get hit rates at current loudness and +/-5 dB around current loudness
    const hitRateCurrent = hitProportionAtLoudness(testData, loudness);
    const hitRateLower = hitProportionAtLoudness(testData, loudness - 5);
    const hitRateUpper = hitProportionAtLoudness(testData, loudness + 5);

    // DEBUG LOGGING
    // const currTestData = testData[testData.length-1];
    // console.log('trialIndex', currTestData.trialIndex, 'frequency', currTestData.frequency,
    //   'loudness', currTestData.loudness, 'didUserRespond', currTestData.didUserRespond,
    //   'responseTime', currTestData.responseTime, 'hitRateCurrent', hitRateCurrent,
    //   'hitRateLower', hitRateLower, 'hitRateUpper', hitRateUpper, 'numTrialsCurrent', numTrialsCurrent,
    //   'numTrialsLower', numTrialsLower, 'numTrialsUpper', hitRateUpper)

    // NOW EVALUATE CRITERIA AND RETURN THRESHOLD
    if (hitsBelow15 > 2) {
      //console.log('hits below 15 fulfilled, threshold is', 10)
      postTestData(testData, 10);
      return 10;
    }
    if (missesAbove80 > 2) {
      // PRODUCTION RETEST CRITERION FOR RETEST
      // 90 dB threshold observed at 500, 1000, or 2000 Hz
      // if (constants['lowFreqList'].includes(frequency)) {
      //   setRetestList([...retestList, frequency]);
      // }

      //console.log('misses above 80 fulfilled threshold is', 90)
      postTestData(testData, 90);
      return 90;
    }

    if (maxTrialCheck) {
      // get array of loudness values with hit
      const loudnessValuesWithHit = testData
        .filter((trial) => trial.didUserRespond === true)
        .map((trial) => trial.loudness);

      // get number of hits at each loudness
      const loudnessHits = loudnessValuesWithHit.map((x) =>
        numHitsAtLoudness(testData, x),
      );

      // max trials reached so algorithm should make its best guess what the true threshold is
      // the more hits a loudness value has, the more times there was a miss 5 dB below it
      // therefore, the loudness value with most hits is a good candidate for threshold
      // if there is a tie, take the smallest value to err on the side of less hearing loss
      // (avoids over-amplification in hearing aid)
      let smallestLoudnessWithMostHits;
      let maxHits = 0;

      for (let i = 0; i < loudnessHits.length; i++) {
        const hits = loudnessHits[i];

        if (
          (hits && hits > maxHits) ||
          (hits === maxHits &&
            smallestLoudnessWithMostHits &&
            loudnessValuesWithHit[i] < smallestLoudnessWithMostHits)
        ) {
          smallestLoudnessWithMostHits = loudnessValuesWithHit[i];
          maxHits = hits;
        }
      }
      // the true threshold was not determined, so let's retest it
      // PRODUCTION RETEST CRITERION FOR RETEST
      // max trials reached
      // setRetestList([...retestList, frequency]);

      postTestData(testData, smallestLoudnessWithMostHits);
      return smallestLoudnessWithMostHits;
    }

    // ONLY EVALUATE HIT RATES IF AT LEAST 1 MISS HAS BEEN RECORDED AT 5 dB BELOW CURRENT VALUE
    if (numTrialsLower > 0) {
      // >=2 trials only: exactly 2 → strict 100% at current; >2 → majority (>50%) hits
      const currentLoudnessCheck =
        (numTrialsCurrent === 2 && hitRateCurrent === 1) ||
        (numTrialsCurrent > 2 &&
          hitRateCurrent != null &&
          hitRateCurrent > 0.5);

      // >=2 trials only: exactly 2 → strict 0% at current-5 dB; >2 → majority (<50%) hits
      const lowerLoudnessCheck =
        (numTrialsLower === 2 && hitRateLower === 0) ||
        (numTrialsLower > 2 &&
          hitRateLower != null &&
          hitRateLower < 0.5);

      // if both conditions are met, their threshold is the current loudness value
      if (currentLoudnessCheck && lowerLoudnessCheck) {
        // PRODUCTION RETEST CRITERION FOR RETEST
        // if left ear is being tested and frequency is 500, 1000, or 2000 Hz, ...
        // check to see if an asymmetry of 30 dB or more has been observed
        // if (constants['asymFreqList'].includes(testData[0].frequency)) {
        //   console.log('running asymmetry check 484')

        //   const freq = testData[0].frequency.slice(1); // Get the numeric part of the frequency
        //   const rightEarValue = state.hearingresultdata[`first_test_right_${freq}__c`];
        //   // left ear threshold is current loudness
        //   const leftEarValue = parseInt(loudness, 10)

        //   console.log('rightEarValue', rightEarValue)
        //   console.log('leftEarValue', leftEarValue)

        //   if (rightEarValue !== undefined && leftEarValue !== undefined) {
        //     const diff = Math.abs(rightEarValue - leftEarValue);
        //     if (diff >= 30) {
        //       console.log('diff greater than 30, adding only larger threshold frequency to retestList');
        //       const largerEarFreq = rightEarValue > leftEarValue ? `R${freq}` : `L${freq}`;
        //       setRetestList((prevRetestList) => [...new Set([...prevRetestList, largerEarFreq])]);
        //     }
        //   }
        // }

        postTestData(testData, loudness);
        return loudness;
      }
    }

    // ALSO CHECK UPPER
    if (numTrialsUpper > 0) {
      // >=2 trials only: exactly 2 → strict 0% at current; >2 → majority (<50%) misses
      const currentLoudnessCheck =
        (numTrialsCurrent === 2 && hitRateCurrent === 0) ||
        (numTrialsCurrent > 2 &&
          hitRateCurrent != null &&
          hitRateCurrent < 0.5);

      // >=2 trials only: exactly 2 → strict 100% at current+5 dB; >2 → majority (>50%) hits
      const upperLoudnessCheck =
        (numTrialsUpper === 2 && hitRateUpper === 1) ||
        (numTrialsUpper > 2 &&
          hitRateUpper != null &&
          hitRateUpper > 0.5);

      // if both conditions are met, their threshold is the current loudness value
      if (currentLoudnessCheck && upperLoudnessCheck) {
        // PRODUCTION RETEST CRITERION FOR RETEST
        // if left ear is being tested and frequency is 500, 1000, or 2000 Hz, ...
        // check to see if an asymmetry of 30 dB or more has been observed
        // if (constants['asymFreqList'].includes(testData[0].frequency)) {
        //   console.log('running asymmetry check 523')

        //   const freq = testData[0].frequency.slice(1); // Get the numeric part of the frequency
        //   const rightEarValue = state.hearingresultdata[`first_test_right_${freq}__c`];
        //   // threshold for left ear is 5 dB above current loudness
        //   const leftEarValue = parseInt(loudness) + 5

        //   console.log('rightEarValue', rightEarValue)
        //   console.log('leftEarValue', leftEarValue)

        //   if (rightEarValue !== undefined && leftEarValue !== undefined) {
        //     const diff = Math.abs(rightEarValue - leftEarValue);
        //     if (diff >= 30) {
        //       console.log('diff greater than 30, adding only larger threshold frequency to retestList');
        //       const largerEarFreq = rightEarValue > leftEarValue ? `R${freq}` : `L${freq}`;
        //       setRetestList((prevRetestList) => [...new Set([...prevRetestList, largerEarFreq])]);
        //     }
        //   }
        // }

        //console.log('current and upper checks fulfilled, threshold is', parseInt(loudness) + 5)
        postTestData(testData, loudness + 5);
        // their threhsold is the value 5 dB above current loudness
        return loudness + 5;
      }
    }
  };

  const handleEndTest = () => {
    // sequence ended so threshold was posted
    setDataPosted(true);
    // reset testData to [] for next sequence
    resetTestData();
    // remove the frequency that was just tested from the frequency list
    const updatedFreqList = freqList.filter((freq) => freq !== frequency);
    if (isTraining) {
      setIsTraining(false);
    } else {
      setFreqList(updatedFreqList);
    }
    if (updatedFreqList.length === 0) {
      // if (retestList.length === 0 || isRetest) {
      // if all frequencies have been tested, end the test and navigate to results
      dispatch({ type: 'UPDATE_RESULTSURL', payload: getResultsUrl() });
      dispatch({ type: 'UPDATE_OVERALLSCORE', payload: getOverallScore() });
      
      const nextRoute = isB2b ? '/feedback' : '/results';
      navigate(nextRoute); // single navigation

      // nextStep()
      dispatch({ type: 'UPDATE_PROGRESS', payload: 5 });
      dispatch({ type: 'UPDATE_CURRENT_STEP', payload: 4 });

      // } else {
      //   setIsRetest(true);
      //   setFreqList(retestList);
      //   setFrequency(retestList[0]);
      // }
    } else {
      // otherwise set the next frequency
      setFrequency(freqList[0]);
    }
  };

  return {
    testData,
    updateTestData,
    resetTestData,
    loudness,
    setLoudness,
    frequency,
    setFrequency,
    isTraining,
    setIsTraining,
    isRetest,
    setIsRetest,
    checkStoppingCriteria,
    handleEndTest,
    dataPosted,
    setDataPosted,
    freqList,
  };
};

export default useTest;
